import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Grid2,
} from "@mui/material";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import ChartCalendar from "../../DigitalTwinV3/ChartCalendar";
import { PrimaryGranularityButton } from "../../../Theme/DigitalTwinStyles";
import LocationFilter from "../../BmsFaults/Analytics/DetailsFilters/LocationFilter";
import OperationalStatusFilter from "../../Common/OperationalStatusFilter";
import CustomIcon from "../../../Components/CustomIcon";
import { FilterIconSvg } from "../../../Features/Table/Icons";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { analyticsCalendarEndDate } from "../../../Helper/DatePicker/DateConverter";

const GRANULARITY_OPTIONS = ["Daily", "Weekly", "Monthly"];

const StateOfUseTrendDetailsSidebar = ({
  isExpanded,
  onToggle,
  startDate,
  endDate,
  onDateChange,
  granularity,
  onGranularityChange,
  allOnboardingLocationsHierarchy,
  selectedLocations,
  onLocationChange,
  selectedOperationalStatus,
  onOperationalStatusChange,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const initialLocationRef = useRef(selectedLocations || []);
  const initialOperationalStatusRef = useRef(selectedOperationalStatus || []);

  const handleReset = () => {
    if (onLocationChange) onLocationChange(initialLocationRef.current);
    if (onOperationalStatusChange) onOperationalStatusChange(initialOperationalStatusRef.current);
  };

  return (
    <>
      {/* ── Collapse / expand toggle ──────────────────────────────────────── */}
      <IconButton
        onClick={onToggle}
        sx={{
          position: "absolute",
          right: isExpanded ? "320px" : "0",
          zIndex: 1000,
          backgroundColor: "transparent",
          width: "20px",
          height: "20px",
          transition: "right 0.3s ease-in-out",
          boxShadow: "none",
          "&:hover": { backgroundColor: "transparent" },
          "&:focus": { backgroundColor: "transparent" },
        }}
      >
        <PrimaryTooltip
          title={isExpanded ? "Collapse Panel" : "Expand Panel"}
          placement="bottom"
        >
          <KeyboardTabIcon
            sx={{
              fontSize: 20,
              color: "#494949",
              transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
              "&:hover": { color: "#6D72F6" },
            }}
          />
        </PrimaryTooltip>
      </IconButton>

      {/* ── Sidebar panel ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: isExpanded ? "320px" : "0px",
          transition: "width 0.3s ease-in-out",
          borderLeft: isExpanded ? "1px solid #E6E6E6" : "none",
          backgroundColor: "#FFFFFF",
          height: "100%",
          overflow: "hidden",
          flexShrink: 0,
          my: -2,
          mr: -3,
          position: "fixed",
          right: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "320px",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          {/* Header tabs */}
          <Box sx={{ px: 3, flexShrink: 0 }}>
            <Box sx={{ mx: -3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#8D8D8D",
                    p: 2,
                    "&.Mui-selected": { color: "#494949" },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#494949" },
                }}
              >
                <Tab label="Settings" />
              </Tabs>
            </Box>
            <Divider sx={{ borderColor: "#E6E6E6", mt: 0, mx: -3 }} />
          </Box>

          {/* Scrollable body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "scroll",
              pb: 3,
              "&::-webkit-scrollbar": { width: "6px" },
            }}
          >
            {activeTab === 0 && (
              <Box>
                {/* ── Time Configuration ───────────────────────────────── */}
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: "none",
                    borderBottom: "1px solid #E6E6E6",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { mt: 0, mb: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardDoubleArrowDownIcon />}
                    sx={{
                      "&.Mui-expanded": { minHeight: "auto" },
                      "& .MuiAccordionSummary-content": { margin: "20px 0" },
                    }}
                  >
                    <Grid2 container alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "4px",
                          backgroundColor: "#EFF0FF",
                          p: 0.75,
                        }}
                      >
                        <CustomIcon
                          name="round-clock-icon"
                          width="15"
                          height="15"
                          stroke="#4D44D8"
                        />
                      </Box>
                      <Typography
                        variant="fw700s14c3D4B5A"
                        sx={{ color: "#2E2E2E", lineHeight: "20px" }}
                      >
                        Time Configuration
                      </Typography>
                    </Grid2>src/Pages/FleetPerformance/FleetPerformance.jsx
                  </AccordionSummary>

                  <AccordionDetails sx={{ paddingTop: 0 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {/* Date */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography
                          variant="fw500s12lh24c686868"
                          sx={{ mb: 1, fontSize: "13px", lineHeight: "20px" }}
                        >
                          Date:
                        </Typography>
                        <ChartCalendar
                          startDate={startDate}
                          endDate={endDate}
                          onDateChange={onDateChange}
                          disableAfterDate={analyticsCalendarEndDate()}
                          forcePresets={true}
                        />
                      </Box>

                      {/* Granularity */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography
                          variant="fw500s12lh24c686868"
                          sx={{ mb: 1, fontSize: "13px", lineHeight: "20px" }}
                        >
                          Granularity:
                        </Typography>
                        <Grid2
                          container
                          sx={{ background: "#F9F9F9", padding: "4px", borderRadius: "6px" }}
                        >
                          {GRANULARITY_OPTIONS.map((option) => (
                            <PrimaryGranularityButton
                              key={option}
                              onClick={() => onGranularityChange(option)}
                              active={granularity === option}
                              sx={{ width: "90px" }}
                            >
                              <Typography textTransform="capitalize">{option}</Typography>
                            </PrimaryGranularityButton>
                          ))}
                        </Grid2>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* ── Filters (Location only) ───────────────────────────── */}
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { mt: 0, pb: 3 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardDoubleArrowDownIcon />}
                    sx={{
                      "&.Mui-expanded": { minHeight: "auto" },
                      "& .MuiAccordionSummary-content": { margin: "20px 0" },
                    }}
                  >
                    <Grid2
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: "100%", pr: 1 }}
                    >
                      <Grid2 container alignItems="center" gap={1.5}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "4px",
                            backgroundColor: "#EFF0FF",
                            p: 0.75,
                          }}
                        >
                          <FilterIconSvg sx={{ color: "#4D44D8" }} />
                        </Box>
                        <Typography
                          variant="fw700s14c3D4B5A"
                          sx={{ color: "#2E2E2E", lineHeight: "20px" }}
                        >
                          Filters
                        </Typography>
                      </Grid2>
                      <Typography
                        variant="fw500s12lh246D72F6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReset();
                        }}
                        sx={{ cursor: "pointer", "&:hover": { color: "#4D44D8" } }}
                      >
                        Reset
                      </Typography>
                    </Grid2>
                  </AccordionSummary>

                  <AccordionDetails sx={{ paddingTop: 0 }}>
                    <LocationFilter
                      locations={allOnboardingLocationsHierarchy || []}
                      selectedLocations={selectedLocations || []}
                      onLocationChange={(loc) => {
                        if (onLocationChange) onLocationChange(loc);
                      }}
                    />
                    <OperationalStatusFilter
                      selectedOperationalStatus={selectedOperationalStatus || []}
                      onOperationalStatusChange={(status) => {
                        if (onOperationalStatusChange) onOperationalStatusChange(status);
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default StateOfUseTrendDetailsSidebar;
